// ** React Imports
import { ReactNode, createContext, useContext, useState, useEffect } from 'react'

// ** Next Imports
import Head from 'next/head'
import { Router } from 'next/router'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'

// ** Store Imports
import { persistor, store } from 'src/store'
import { Provider } from 'react-redux'

// ** Loader Import
import NProgress from 'nprogress'

// ** Emotion Imports
import { CacheProvider } from '@emotion/react'
import type { EmotionCache } from '@emotion/cache'

// ** Config Imports
import 'src/configs/i18n'
import { defaultACLObj } from 'src/configs/acl'
import themeConfig from 'src/configs/themeConfig'

// ** Fake-DB Import
import 'src/@fake-db'

// ** Third Party Import
import { Toaster } from 'react-hot-toast'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

// ** Component Imports
import UserLayout from 'src/layouts/UserLayout'
import AclGuard from 'src/@core/components/auth/AclGuard'
import ThemeComponent from 'src/@core/theme/ThemeComponent'
import AuthGuard from 'src/@core/components/auth/AuthGuard'
import GuestGuard from 'src/@core/components/auth/GuestGuard'

// ** Spinner Import
import Spinner from 'src/@core/components/spinner'

// ** Contexts
import { AuthProvider } from 'src/context/AuthContext'
import { SettingsConsumer, SettingsProvider } from 'src/@core/context/settingsContext'

// ** Styled Components
import ReactHotToast from 'src/@core/styles/libs/react-hot-toast'

// ** Utils Imports
import { createEmotionCache } from 'src/@core/utils/create-emotion-cache'

// ** Components Imports
import IframeFlowHandler from 'src/components/IframeFlowHandler'
import ErrorBoundary from 'src/components/ErrorBoundary'
import { GYGPTChatbot } from '@/src/modules/ai-insights/components/GYGPTChatbot'
import { useAuth } from '@/src/hooks/useAuth'
import { useGrowthBook } from '@/src/context/GrowthBookProvider'
import { FeatureFlag } from '@/src/types/common'

// ** Prismjs Styles
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'

// ** React Perfect Scrollbar Style
import 'react-perfect-scrollbar/dist/css/styles.css'

import 'src/iconify-bundle/icons-bundle-react'

// ** Global css styles
import '../../styles/globals.css'
import '../styles/iframeStyles.css'
import { WebSocketProvider } from '../context/WebSocketContext'
import { PersistGate } from 'redux-persist/integration/react'
import { GrowthBookProvider } from '../context/GrowthBookProvider'
import GtagScript from '@/src/googleAnalytics/gtag'
import { validateGtagConfig } from '@/src/googleAnalytics/config'
import { setSessionCountryFromStorage } from '../@core/lib/session'
import { initDatadog } from '@/src/observability/datadog'

// ** Extend App Props with Emotion
type ExtendedAppProps = AppProps & {
  Component: NextPage
  emotionCache: EmotionCache
}

type GuardProps = {
  authGuard: boolean
  guestGuard: boolean
  children: ReactNode
}

const clientSideEmotionCache = createEmotionCache()

// ** Pace Loader
if (themeConfig.routingLoader) {
  Router.events.on('routeChangeStart', () => {
    NProgress.start()
  })
  Router.events.on('routeChangeError', () => {
    NProgress.done()
  })
  Router.events.on('routeChangeComplete', () => {
    NProgress.done()
  })
}

const Guard = ({ children, authGuard, guestGuard }: GuardProps) => {
  if (guestGuard) {
    return <GuestGuard fallback={<Spinner />}>{children}</GuestGuard>
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>
  } else {
    return (
      <AuthGuard fallback={<Spinner />}>
        <WebSocketProvider>{children}</WebSocketProvider>
      </AuthGuard>
    )
  }
}

// ** Global GYGPT (AI Insights) floating widget — opt-in via env so it stays out of production until ready.
// Restaurant page chatbot (ProactiveChatWidget) is unaffected. Set NEXT_PUBLIC_ENABLE_GLOBAL_GYGPT_CHATBOT=true locally to develop.
// This component must be rendered inside AuthProvider and GrowthBookProvider context
const GlobalChatbot: React.FC = () => {
  const auth = useAuth()
  const growthbook = useGrowthBook()
  const isRestaurantInsightsEnabled = growthbook?.isOn(FeatureFlag.RESTAURANT_INSIGHTS) || false

  if (process.env.NEXT_PUBLIC_ENABLE_GLOBAL_GYGPT_CHATBOT !== 'true') {
    return null
  }

  if (!auth.user || !isRestaurantInsightsEnabled) {
    return null
  }

  return <GYGPTChatbot />
}

interface GlobalStateContextType {
  isChildPage: boolean
  isProductPage: boolean
  isFranchisePage: boolean
  isGlobalBoolean: boolean
  setChildPage: (value: boolean) => void
  setProductPage: (value: boolean) => void
  setFranchisePage: (value: boolean) => void
  setGlobalBoolean: (value: boolean) => void
}

const GlobalStateContext = createContext<GlobalStateContextType | undefined>(undefined)

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext)
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider')
  }

  return context
}

// ** Configure JSS & ClassName
const App = (props: ExtendedAppProps) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props

  // Validate gtag configuration on app startup
  validateGtagConfig()

  // Register service worker for push notifications
  useEffect(() => {
    try {
      setSessionCountryFromStorage()
    } catch (_) {}

    initDatadog()

    if ('serviceWorker' in navigator && typeof window !== 'undefined') {
      navigator.serviceWorker
        .register('/sw.js')
        .then(registration => {
          // Service Worker registered successfully
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('Service Worker registered successfully:', registration)
          }
        })
        .catch(error => {
          // Service Worker registration failed
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('Service Worker registration failed:', error)
          }
        })
    }
  }, [])

  // Suppress Google Analytics fetch errors (common with ad blockers, network issues, etc.)
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason
      const errorMessage = error?.message || String(error)
      const errorStack = error?.stack || ''

      // Check if this is a Google Analytics fetch error
      const isGoogleAnalyticsError =
        errorMessage.includes('google-analytics.com') ||
        errorMessage.includes('googletagmanager.com') ||
        errorStack.includes('google-analytics.com') ||
        errorStack.includes('googletagmanager.com') ||
        (errorMessage.includes('Failed to fetch') &&
          (errorMessage.includes('g/collect') || errorMessage.includes('gtag')))

      // Check if this is a browser extension error (harmless)
      const isExtensionError =
        errorMessage.includes('listener indicated an asynchronous response') ||
        errorMessage.includes('message channel closed') ||
        errorStack.includes('chrome-extension://') ||
        errorStack.includes('moz-extension://')

      if (isGoogleAnalyticsError || isExtensionError) {
        // Prevent the error from appearing in console
        event.preventDefault()

        // Silently ignore - these errors are expected and don't affect functionality
        return
      }
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    // Suppress browser extension errors (harmless)
    const handleExtensionError = (event: ErrorEvent) => {
      const errorMessage = event.message || ''

      // Suppress browser extension async response errors
      if (
        errorMessage.includes('listener indicated an asynchronous response') ||
        errorMessage.includes('message channel closed')
      ) {
        event.preventDefault()

        return false
      }
    }

    window.addEventListener('error', handleExtensionError)

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('error', handleExtensionError)
    }
  }, [])

  // Variables
  const contentHeightFixed = Component.contentHeightFixed ?? false
  const getLayout =
    Component.getLayout ?? (page => <UserLayout contentHeightFixed={contentHeightFixed}>{page}</UserLayout>)

  const setConfig = Component.setConfig ?? undefined

  const authGuard = Component.authGuard ?? true

  const guestGuard = Component.guestGuard ?? false

  const aclAbilities = Component.acl ?? defaultACLObj

  const [isGlobalBoolean, setGlobalBoolean] = useState<boolean>(false)
  const [isChildPage, setChildPage] = useState<boolean>(false)
  const [isProductPage, setProductPage] = useState<boolean>(false)
  const [isFranchisePage, setFranchisePage] = useState<boolean>(false)

  return (
    <GlobalStateContext.Provider
      value={{
        isChildPage,
        setChildPage,
        isProductPage,
        isFranchisePage,
        setProductPage,
        setFranchisePage,
        isGlobalBoolean,
        setGlobalBoolean
      }}
    >
      <Provider store={store}>
        <PersistGate loading={<Spinner />} persistor={persistor}>
          <CacheProvider value={emotionCache}>
            <Head>
              <title>{themeConfig.templateName}</title>
              <meta name='description' content={themeConfig.templateName} />
              <meta name='viewport' content='initial-scale=1, width=device-width' />
            </Head>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <AuthProvider>
                <SettingsProvider {...(setConfig ? { pageSettings: setConfig() } : {})}>
                  <SettingsConsumer>
                    {({ settings }) => {
                      return (
                        <ThemeComponent settings={settings}>
                          <ErrorBoundary>
                            <GrowthBookProvider>
                              <GtagScript />
                              <IframeFlowHandler />
                              <Guard authGuard={authGuard} guestGuard={guestGuard}>
                                <AclGuard aclAbilities={aclAbilities} guestGuard={guestGuard} authGuard={authGuard}>
                                  {getLayout(<Component {...pageProps} />)}
                                </AclGuard>
                              </Guard>
                              <ReactHotToast>
                                <Toaster
                                  position={settings.toastPosition}
                                  toastOptions={{ className: 'react-hot-toast' }}
                                />
                              </ReactHotToast>
                              {/* Global GYGPT — only when NEXT_PUBLIC_ENABLE_GLOBAL_GYGPT_CHATBOT=true, authenticated, and Restaurant Insights flag on */}
                              <GlobalChatbot />
                            </GrowthBookProvider>
                          </ErrorBoundary>
                        </ThemeComponent>
                      )
                    }}
                  </SettingsConsumer>
                </SettingsProvider>
              </AuthProvider>
            </LocalizationProvider>
          </CacheProvider>
        </PersistGate>
      </Provider>
    </GlobalStateContext.Provider>
  )
}

export default App
